import React, { useState } from "react";

/**
 * Form validation, all at once and beside the field (M3 follow-on #1): on
 * Save, every missing or invalid field is marked with its message directly
 * under it, and the page scrolls to the first. The messages are the server's
 * own wording (checks.ts); the server still checks everything again.
 */

/** Field id → message, in the order the fields appear on the form. */
export type Problems = Record<string, string>;
/** [the field's element id, whether it fails, the message]. */
export type Rule = readonly [id: string, failing: boolean, message: string];

/** The first failing message for each field, in rule (form) order. */
export function problemsOf(rules: readonly (Rule | false | null | undefined)[]): Problems {
  const out: Problems = {};
  for (const r of rules) if (r && r[1] && !(r[0] in out)) out[r[0]] = r[2];
  return out;
}

/**
 * The form's problems: none until the first Save; from then on they follow
 * the fields as they are corrected. `check()` on Save: true when it may be
 * sent; otherwise it scrolls to (and focuses) the first problem.
 */
export function useProblems(rules: readonly (Rule | false | null | undefined)[]): { problems: Problems; check: (now?: readonly (Rule | false | null | undefined)[]) => boolean; reset: () => void } {
  const [tried, setTried] = useState(false);
  const problems = tried ? problemsOf(rules) : {};
  return {
    problems,
    /** `now`: the rules this Save goes by, when the button itself changes them (receipt only vs receive and confirm). */
    check(now = rules) {
      setTried(true);
      const first = Object.keys(problemsOf(now))[0];
      if (first) scrollToField(first);
      return !first;
    },
    reset: () => setTried(false),
  };
}

export function scrollToField(id: string): void {
  // After the render that shows the messages.
  setTimeout(() => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const target = el.matches("input, select, textarea, button") ? el : el.querySelector<HTMLElement>("input, select, textarea, button");
    target?.focus({ preventScroll: true });
  }, 0);
}

/** The message under a field (nothing when it is fine). */
export function FieldProblem({ id, problems }: { id: string; problems: Problems }): React.ReactElement | null {
  const m = problems[id];
  return m ? <p id={`${id}-problem`} className="field-error">{m}</p> : null;
}

/** aria-invalid and the message's id, for the field's own input. */
export function invalid(id: string, problems: Problems): { "aria-invalid"?: true; "aria-describedby"?: string } {
  return problems[id] ? { "aria-invalid": true, "aria-describedby": `${id}-problem` } : {};
}
